import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AccesoEnElMenu, Aviso, Bloques, Contador, Membresia, Navegacion, Operaciones, PreferenciasMenu, ResumenAvisos, Suscripcion, Yo } from '../api/operaciones';
import { ErrorApi } from '../api/problema';
import { permisosDe, type Permisos } from '../permisos/permisos';
import { faltanDosPasos } from './acceso';
import { esEstadoRevisable, type TipoEstado } from './estados';
import { franjasDe, type Franja } from './franjas';
import { construirMenu, pantallaDe, rutaInicial, type Menu, type Pantalla } from './navegacion';
import { enSoloLectura } from './soloLectura';

/**
 * Datos del panel de un negocio, sin nada visual (los pinta el marco, G127, ADR-0050): la cuenta (`getMe`), el menú y
 * los permisos (`getMyNavigation`, G97, G98, con las preferencias de la persona ya aplicadas, G129, y el acceso de
 * plataforma abierto, G137), el modo (`listEntitlements`), las fechas y el consumo para las franjas
 * (`getSubscription` y `getUsage`, solo con `plan.ver`; 0.5, P0-9) y los avisos de la campana (las cinco operaciones de
 * `entregas.yaml`). Una ruta que el menú no da (o la raíz) lleva a la primera entrada del menú: lo que el rol no puede
 * ver no aparece. Un 401 es sesión caducada (vuelta a entrar) y un `two_factor_required`, Soporte sin los dos pasos.
 * Con datos simulados, `?estado=vacia|cargando|error|sin-permiso|no-contratado` fuerza un estado para revisar el diseño.
 *
 * Preferencias del menú (G129): `guardarPreferencias` las manda con `updateMyNavigationPreferences` y vuelve a pedir el
 * menú, que llega con ellas aplicadas; cerrar una franja amarilla la añade a `dismissed_notices`.
 */
export interface OpcionesDatosNegocio {
    ops: Operaciones;
    negocio: string;
    ruta: string;
    busqueda: URLSearchParams;
    navegar: (ruta: string, reemplazar?: boolean) => void;
    simulacion: boolean;
    /** La sesión ya no vale (401): vuelta al inicio de sesión. */
    onSesionCaducada: () => void;
    /** Soporte o Superadmin sin los dos pasos (`403 two_factor_required`, G65). */
    onFaltanDosPasos: () => void;
    /** Un error que la pantalla debe contar (aviso breve). */
    onError: (texto: string) => void;
    /** Rutas del panel que no son del menú (pantallas sueltas): no se llevan a la primera entrada. */
    rutasPropias?: readonly string[];
    ahora?: () => Date;
}

/**
 * Lo que da `listEntitlements`. Pendiente P0-9: la recomendación es que traiga también `trial_ends_at` y `grace_ends_at`
 * para que quien no tiene `plan.ver` vea los días en la franja; la simulación ya los da y aquí se leen si llegan.
 */
export type BloquesConFechas = Bloques & { trial_ends_at?: string | null; grace_ends_at?: string | null };

export interface DatosCargados {
    yo: Yo;
    navegacion: Navegacion;
    bloques: BloquesConFechas | null;
    suscripcion: Suscripcion | null;
    contadores: Contador[];
    /** Nombre del negocio cuando quien mira no es miembro (acceso de plataforma): `getBusiness`. */
    nombreNegocio: string | null;
}

export interface DatosNegocio {
    datos: DatosCargados | null;
    /** Error al cargar (para su estado de página, `estadoDeError`); null si no hay. */
    error: unknown;
    reintentar: () => void;
    menu: Menu;
    permisos: Permisos;
    pantalla: Pantalla;
    rutaInicial: string | null;
    membresias: readonly Membresia[];
    membresia: Membresia | undefined;
    /** El negocio está en solo lectura (chip «Solo lectura» y botones de escribir desactivados). */
    soloLectura: boolean;
    /** Nombre del negocio: el de la membresía o, con un acceso de plataforma, el de getBusiness. */
    nombreNegocio: string;
    /** Acceso de plataforma abierto (G137), o null. */
    acceso: AccesoEnElMenu | null;
    /** La persona puede personalizar su menú: es miembro (con un acceso de plataforma, `403`, G129). */
    personalizable: boolean;
    /** Lee lo guardado (para «Restablecer», apagado si no hay nada). */
    leerPreferencias: () => Promise<PreferenciasMenu>;
    /** Guarda las preferencias y vuelve a pedir el menú. */
    guardarPreferencias: (cambios: PreferenciasMenu) => Promise<void>;
    /** Claves de las franjas cerradas (`dismissed_notices`). */
    franjasCerradas: ReadonlySet<string>;
    /** Cierra una franja amarilla: queda recordada en el servidor y sigue en la campana. */
    cerrarFranja: (clave: string) => void;
    /** Franjas del estado de la cuenta, de más a menos grave (0.5). */
    franjas: Franja[];
    /** Estado forzado con `?estado=` (solo con datos simulados). */
    estadoForzado: TipoEstado | null;
    campana: {
        sinLeer: number;
        /** null hasta que se abre la lista. */
        avisos: Aviso[] | null;
        recargar: () => void;
        leerTodos: () => void;
        abrir: (aviso: Aviso) => void;
        archivar: (aviso: Aviso) => void;
    };
}

const RESUMEN_VACIO: ResumenAvisos = { unread: 0, by_severity: { critical: 0, warning: 0, info: 0 } };

export const TEXTOS_ERROR_AVISOS = {
    cargar: 'No he podido cargar los avisos. Prueba de nuevo.',
    leerTodos: 'No he podido marcar los avisos como leídos. Prueba de nuevo.',
    leer: 'No he podido marcar el aviso como leído. Prueba de nuevo.',
    archivar: 'No he podido archivar el aviso. Prueba de nuevo.',
} as const;

/** Texto del aviso breve si no se puede guardar el menú (la exportación no lo trae: lo guardaba en el navegador). */
export const TEXTO_ERROR_MENU = 'No he podido guardar tu menú. Prueba de nuevo.';

/** Permiso con el que se ven el plan, sus fechas y el consumo (`getSubscription`, `getUsage`). */
const PLAN_VER = 'plan.ver';

const ahoraPorDefecto = () => new Date();

/**
 * Los avisos de la campana son de Entregas (fase 2). Mientras su API no exista, sus operaciones responden 404: la campana
 * se queda con las franjas que no caben arriba y sin avisos, sin contarlo como error. Los avisos personales (G238, PL-2)
 * esperan a Entregas y a su decisión.
 */
export function sinAvisosTodavia(e: unknown): boolean {
    return e instanceof ErrorApi && e.estado === 404;
}

export function useDatosNegocio({
    ops,
    negocio,
    ruta,
    busqueda,
    navegar,
    simulacion,
    onSesionCaducada,
    onFaltanDosPasos,
    onError,
    rutasPropias = [],
    ahora = ahoraPorDefecto,
}: OpcionesDatosNegocio): DatosNegocio {
    const [datos, setDatos] = useState<DatosCargados | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [resumen, setResumen] = useState<ResumenAvisos>(RESUMEN_VACIO);
    const [avisos, setAvisos] = useState<Aviso[] | null>(null);

    const caducada = useCallback(
        (e: unknown) => {
            if (e instanceof ErrorApi && e.codigo === 'unauthenticated') {
                onSesionCaducada();
                return true;
            }
            if (faltanDosPasos(e)) {
                onFaltanDosPasos();
                return true;
            }
            return false;
        },
        [onSesionCaducada, onFaltanDosPasos],
    );

    useEffect(() => {
        let vivo = true;
        Promise.all([ops.getMe(), ops.getMyNavigation(negocio), ops.listEntitlements(negocio).catch(() => null)])
            .then(async ([yo, navegacion, bloques]) => {
                // Fechas y consumo para las franjas: solo con `plan.ver` (si fallan, las franjas salen sin ellos).
                const conPlan = permisosDe(navegacion).puede(PLAN_VER);
                const miembro = yo.memberships.some((m) => m.business_id === negocio);
                const [suscripcion, uso, delNegocio] = await Promise.all([
                    conPlan ? ops.getSubscription(negocio).catch(() => null) : null,
                    conPlan ? ops.getUsage(negocio).catch(() => null) : null,
                    // Quien entra con un acceso de plataforma no es miembro: el nombre, de getBusiness (G137).
                    miembro ? null : ops.getBusiness(negocio).catch(() => null),
                ]);
                if (vivo) {
                    setDatos({ yo, navegacion, bloques, suscripcion, contadores: uso?.data ?? [], nombreNegocio: delNegocio?.name ?? null });
                }
            })
            .catch((e: unknown) => {
                if (vivo && !caducada(e)) {
                    setError(e);
                }
            });
        ops.getMyNoticeSummary(negocio)
            .then((r) => {
                if (vivo) {
                    setResumen(r);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento, caducada]);

    const recargarMenu = useCallback(
        () =>
            ops.getMyNavigation(negocio).then((navegacion) => {
                setDatos((d) => (d === null ? d : { ...d, navegacion }));
            }),
        [ops, negocio],
    );

    const guardarPreferencias = useCallback(
        (cambios: PreferenciasMenu) =>
            ops
                .updateMyNavigationPreferences(negocio, cambios)
                .then(recargarMenu)
                .catch((e: unknown) => {
                    if (!caducada(e)) {
                        onError(TEXTO_ERROR_MENU);
                    }
                }),
        [ops, negocio, recargarMenu, caducada, onError],
    );

    const menu = useMemo(() => construirMenu(datos?.navegacion ?? { items: [] }), [datos]);
    // Permisos efectivos de getMyNavigation (G98): solo para ocultar botones; la API protege cada petición.
    const permisos = useMemo(() => permisosDe(datos?.navegacion ?? null), [datos]);
    const pantalla = pantallaDe(menu, ruta);
    const inicial = rutaInicial(menu);

    useEffect(() => {
        if (datos !== null && pantalla.tipo === 'desconocida' && inicial !== null && !rutasPropias.includes(ruta)) {
            navegar(inicial, true);
        }
    }, [datos, pantalla.tipo, inicial, navegar, rutasPropias, ruta]);

    const recargar = useCallback(() => {
        ops.listMyNotices(negocio)
            .then((lista) => {
                setAvisos(lista.data.filter((a) => a.state !== 'archived'));
            })
            .catch((e: unknown) => {
                if (!caducada(e)) {
                    setAvisos([]);
                    if (!sinAvisosTodavia(e)) {
                        onError(TEXTOS_ERROR_AVISOS.cargar);
                    }
                }
            });
        ops.getMyNoticeSummary(negocio).then(setResumen).catch(() => undefined);
    }, [ops, negocio, caducada, onError]);

    const sobreAviso = (accion: () => Promise<unknown>, textoError: string) => {
        accion()
            .then(recargar)
            .catch((e: unknown) => {
                if (!caducada(e)) {
                    onError(textoError);
                }
            });
    };

    const membresias = datos?.yo.memberships ?? [];
    const membresia = membresias.find((m) => m.business_id === negocio);
    const modo = datos?.bloques?.mode ?? datos?.navegacion.mode ?? 'operational';
    const estado = datos?.bloques?.status ?? membresia?.subscription_status ?? null;
    const forzado = simulacion ? busqueda.get('estado') : null;
    const sub = datos?.suscripcion ?? null;
    const acceso = datos?.navegacion.platform_access ?? null;
    const nombreNegocio = membresia?.business_name ?? datos?.nombreNegocio ?? '';
    const cerradas = useMemo(() => datos?.navegacion.dismissed_notices ?? [], [datos]);

    const franjas = useMemo(
        () =>
            datos === null
                ? []
                : franjasDe({
                      negocio: nombreNegocio,
                      persona: datos.yo.account.display_name,
                      estado,
                      modo,
                      propietario: membresia?.is_owner === true,
                      puedeVerPlan: permisos.puede(PLAN_VER),
                      finPrueba: sub?.trial_ends_at ?? datos.bloques?.trial_ends_at ?? null,
                      finGracia: sub?.grace_ends_at ?? datos.bloques?.grace_ends_at ?? null,
                      fechaBaja: sub?.cancel_at ?? null,
                      contadores: datos.contadores,
                      acceso,
                      ahora: ahora(),
                  }),
        [datos, membresia, nombreNegocio, acceso, estado, modo, permisos, sub, ahora],
    );

    return {
        datos,
        error,
        reintentar: () => {
            setError(null);
            setIntento((i) => i + 1);
        },
        menu,
        permisos,
        pantalla,
        rutaInicial: inicial,
        membresias,
        membresia,
        soloLectura: enSoloLectura(modo),
        nombreNegocio,
        acceso,
        personalizable: membresia !== undefined && acceso?.viewer !== 'platform',
        leerPreferencias: () => ops.getMyNavigationPreferences(negocio),
        guardarPreferencias,
        franjasCerradas: new Set(cerradas.map((c) => c.key)),
        cerrarFranja: (clave) => {
            if (cerradas.some((c) => c.key === clave)) {
                return;
            }
            const ahoraMismo = ahora().toISOString().replace(/\.\d{3}Z$/, 'Z');
            // Las cerradas se recuerdan en el servidor (máximo 50): se mandan las que ya había más la nueva.
            void guardarPreferencias({ dismissed_notices: [...cerradas, { key: clave, dismissed_at: ahoraMismo }].slice(-50) });
        },
        franjas,
        estadoForzado: esEstadoRevisable(forzado) ? forzado : null,
        campana: {
            sinLeer: resumen.unread,
            avisos,
            recargar,
            leerTodos: () => {
                sobreAviso(() => ops.markAllNoticesRead(negocio), TEXTOS_ERROR_AVISOS.leerTodos);
            },
            abrir: (aviso) => {
                if (aviso.state === 'unread') {
                    sobreAviso(() => ops.markNoticeRead(negocio, aviso.id), TEXTOS_ERROR_AVISOS.leer);
                }
            },
            archivar: (aviso) => {
                sobreAviso(() => ops.archiveNotice(negocio, aviso.id), TEXTOS_ERROR_AVISOS.archivar);
            },
        },
    };
}
